import {
  elementLabel,
  type PreflightAsset,
  type PreflightSettings,
  preflightDocument,
} from "../print/preflight";
import { contrastRatio, deltaEHex, isGradient, opaqueHex, parseHex, toHex } from "../scene/color";
import { boundingRect, type FlatElement, flattenPage, type Rect } from "../scene/geometry";
import { pageSize } from "../scene/pages";
import { smallestFontSize, textFamilies } from "../scene/richtext";
import type { SceneDocument, SceneElement } from "../scene/schema";
import { bindColor, bindTextStyle, brandScale } from "./apply";
import { type BrandTokens, brandColor, brandTextStyle } from "./tokens";

/**
 * "Design prüfen": brand and accessibility checks of a document. Pure; the
 * editor shows the findings live, the MCP server returns them. Fixes are
 * data (`DesignFix`) and applied with `applyDesignFix` on a draft, so the
 * editor turns them into one undo step with live-collaboration ops.
 */

export type DesignCheckCategory = "color" | "font" | "contrast" | "size" | "link" | "print";

export const DESIGN_CHECK_CATEGORY_LABELS: Record<DesignCheckCategory, string> = {
  color: "Farbe nicht in der Marke",
  font: "Schrift nicht in der Marke",
  contrast: "Zu wenig Kontrast",
  size: "Größe & Ränder",
  link: "Unverknüpfte Markenwerte",
  print: "Druck",
};

export type DesignColorSlot = "fill" | "stroke" | "color" | "textStroke";

export type DesignFix =
  /** Set a colour slot to a brand colour and link it. */
  | { kind: "bind-color"; elementId: string; slot: DesignColorSlot; key: string }
  /** Page background to a brand colour, linked. */
  | { kind: "page-background"; pageIndex: number; key: string }
  /** Apply a brand text style (typography only). */
  | { kind: "text-style"; elementId: string; key: string }
  /** Plain text colour (black/white when no brand colour is readable). */
  | { kind: "text-color"; elementId: string; color: string }
  /** Font of a table or chart. */
  | { kind: "font-family"; elementId: string; family: string };

export interface DesignFinding {
  /** Stable per problem (code + target), e.g. for React keys. */
  id: string;
  category: DesignCheckCategory;
  severity: "error" | "warning" | "info";
  code: string;
  message: string;
  pageIndex?: number;
  elementId?: string;
  fix?: DesignFix & { label: string };
}

export interface DesignCheckOptions {
  /** Active brand kit; without one only contrast, size and print checks run. */
  tokens?: BrandTokens | null | undefined;
  /** Image sizes for the print checks (dpi). */
  assets?: Record<string, PreflightAsset> | undefined;
  /** Print settings overriding the document's (provider, bleed …). */
  print?: PreflightSettings | undefined;
  /** Average colour per asset id (for contrast on images); otherwise "nicht prüfbar". */
  imageColors?: Record<string, string> | undefined;
}

/** Pure black and white count as neutral, not as off-brand colours. */
const NEUTRALS = new Set(["#000000", "#ffffff"]);

/** Minimum readable size on a 1080 px wide screen format. */
export const MIN_SCREEN_TEXT_PX = 20;

// ---------------------------------------------------------------------------
// Colours
// ---------------------------------------------------------------------------

export function nearestBrandColor(
  tokens: BrandTokens,
  color: string,
): { key: string; name: string; color: string; deltaE: number } | null {
  let best: { key: string; name: string; color: string; deltaE: number } | null = null;
  for (const entry of tokens.colors) {
    const deltaE = deltaEHex(opaqueHex(color), entry.color);
    if (!best || deltaE < best.deltaE) {
      best = { key: entry.key, name: entry.name, color: entry.color, deltaE };
    }
  }
  return best;
}

function kitColorByValue(tokens: BrandTokens, color: string) {
  const value = opaqueHex(color);
  return tokens.colors.find((entry) => entry.color === value);
}

/** `top` with alpha composited over an opaque `bottom`. */
function blend(top: string, bottom: string, extraAlpha = 1): string {
  const a = parseHex(top);
  const b = parseHex(bottom);
  const alpha = Math.max(0, Math.min(1, a.a * extraAlpha));
  return toHex({
    r: a.r * alpha + b.r * (1 - alpha),
    g: a.g * alpha + b.g * (1 - alpha),
    b: a.b * alpha + b.b * (1 - alpha),
    a: 1,
  });
}

interface ColorUse {
  slot: DesignColorSlot;
  value: string;
  what: string;
}

/** Solid colours of an element that the brand kit could control. */
function colorUses(element: SceneElement): ColorUse[] {
  switch (element.type) {
    case "text": {
      const uses: ColorUse[] = [{ slot: "color", value: element.style.color, what: "Textfarbe" }];
      if (element.style.stroke && element.style.stroke.width > 0) {
        uses.push({ slot: "textStroke", value: element.style.stroke.color, what: "Kontur" });
      }
      return uses;
    }
    case "shape": {
      const uses: ColorUse[] = [];
      if (element.shape !== "line" && !element.placeholder && typeof element.fill === "string") {
        uses.push({ slot: "fill", value: element.fill, what: "Füllung" });
      }
      if (element.stroke && element.stroke.width > 0) {
        uses.push({ slot: "stroke", value: element.stroke.color, what: "Linie" });
      }
      return uses;
    }
    case "image":
      return element.stroke && element.stroke.width > 0
        ? [{ slot: "stroke", value: element.stroke.color, what: "Rahmen" }]
        : [];
    case "icon":
      return [{ slot: "color", value: element.color, what: "Farbe" }];
    case "qr": {
      const uses: ColorUse[] = [{ slot: "color", value: element.color, what: "Farbe" }];
      if (parseHex(element.background).a > 0) {
        uses.push({ slot: "fill", value: element.background, what: "Hintergrund" });
      }
      return uses;
    }
    case "table": {
      const uses: ColorUse[] = [{ slot: "color", value: element.color, what: "Textfarbe" }];
      if (element.header) uses.push({ slot: "fill", value: element.headerFill, what: "Kopfzeile" });
      if (element.borderWidth > 0) {
        uses.push({ slot: "stroke", value: element.borderColor, what: "Linien" });
      }
      return uses;
    }
    default:
      // Groups are flattened; captions, graphics and element types this
      // checker does not know are not checked.
      return [];
  }
}

// ---------------------------------------------------------------------------
// Background beneath a text
// ---------------------------------------------------------------------------

type Backdrop =
  | { kind: "colors"; colors: string[]; source: string }
  | { kind: "unknown"; source: string };

const center = (rect: Rect) => ({ x: rect.x + rect.width / 2, y: rect.y + rect.height / 2 });
const contains = (rect: Rect, point: { x: number; y: number }) =>
  point.x >= rect.x &&
  point.x <= rect.x + rect.width &&
  point.y >= rect.y &&
  point.y <= rect.y + rect.height;

/**
 * Colours behind the centre of a text box: the topmost element beneath it
 * (solid shapes with transparency are composited over what lies below),
 * otherwise the page background. Gradients give all their stops (the worst
 * one counts); images and graphics are "nicht prüfbar" unless their average
 * colour is known.
 */
function backdropAt(
  doc: SceneDocument,
  pageIndex: number,
  below: FlatElement[],
  point: { x: number; y: number },
  imageColors: Record<string, string> | undefined,
): Backdrop {
  const page = doc.pages[pageIndex];
  const pageBackdrop = (): Backdrop => {
    const background = page?.background ?? "#ffffff";
    if (isGradient(background)) {
      return {
        kind: "colors",
        colors: background.stops.map((stop) => blend(stop.color, "#ffffff")),
        source: "zum Seitenhintergrund",
      };
    }
    return {
      kind: "colors",
      colors: [blend(background, "#ffffff")],
      source: "zum Seitenhintergrund",
    };
  };
  for (let index = below.length - 1; index >= 0; index--) {
    const item = below[index];
    if (!item || item.hidden || item.opacity < 0.02) continue;
    if (!contains(boundingRect(item.box), point)) continue;
    const { element } = item;
    const rest = () => backdropAt(doc, pageIndex, below.slice(0, index), point, imageColors);
    const over = (color: string, source: string): Backdrop => {
      const alpha = parseHex(color).a * item.opacity;
      if (alpha >= 0.999) return { kind: "colors", colors: [opaqueHex(color)], source };
      const under = rest();
      if (under.kind === "unknown") return under;
      return {
        kind: "colors",
        colors: under.colors.map((bottom) => blend(color, bottom, item.opacity)),
        source,
      };
    };
    if (element.type === "shape") {
      if (element.shape === "line" || element.placeholder) continue;
      if (isGradient(element.fill)) {
        const fill = element.fill;
        const under = rest();
        if (under.kind === "unknown") return under;
        const base = under.colors[0] ?? "#ffffff";
        return {
          kind: "colors",
          colors: fill.stops.map((stop) => blend(stop.color, base, item.opacity)),
          source: "zum Verlauf darunter",
        };
      }
      if (parseHex(element.fill).a < 0.02) continue;
      return over(element.fill, "zur Form darunter");
    }
    if (element.type === "image") {
      const average = imageColors?.[element.assetId];
      if (average) return over(average, "zum Bild darunter (Durchschnittsfarbe)");
      return { kind: "unknown", source: "einem Bild" };
    }
    if (element.type === "svg") return { kind: "unknown", source: "einer Grafik" };
    if (element.type === "qr" && parseHex(element.background).a > 0.02) {
      return over(element.background, "zum QR-Code darunter");
    }
    // Texts, icons and unknown types: small or thin, they rarely form a backdrop.
  }
  return pageBackdrop();
}

/**
 * Opaque colours behind the centre of an element (for the colour picker's
 * contrast hint); null when an image or graphic lies below.
 */
export function backdropColors(
  doc: SceneDocument,
  elementId: string,
  imageColors?: Record<string, string>,
): string[] | null {
  for (let pageIndex = 0; pageIndex < doc.pages.length; pageIndex++) {
    const flat = flattenPage(doc, pageIndex);
    const index = flat.findIndex((item) => item.element.id === elementId);
    const item = flat[index];
    if (!item) continue;
    const backdrop = backdropAt(
      doc,
      pageIndex,
      flat.slice(0, index),
      center(boundingRect(item.box)),
      imageColors,
    );
    return backdrop.kind === "colors" ? backdrop.colors : null;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Text sizes
// ---------------------------------------------------------------------------

/** Font size in px on a 1080 px reference (screen formats) or in pt (print). */
function textMeasure(doc: SceneDocument, px: number): { value: number; unit: "px" | "pt" } {
  const print = doc.format.print;
  if (print) return { value: (px * print.widthMm * 72) / (doc.format.width * 25.4), unit: "pt" };
  return { value: px / brandScale(doc), unit: "px" };
}

/** WCAG "large text": ≥ 18 pt / 24 px, or ≥ 14 pt / 18.66 px bold. */
export function isLargeText(doc: SceneDocument, fontSizePx: number, fontWeight: number): boolean {
  const { value, unit } = textMeasure(doc, fontSizePx);
  const bold = fontWeight >= 700;
  if (unit === "pt") return value >= 18 || (bold && value >= 14);
  return value >= 24 || (bold && value >= 18.66);
}

/** Brand text style closest to a size (headings for big, body styles for small texts). */
export function closestTextStyle(
  tokens: BrandTokens,
  doc: SceneDocument,
  fontSizePx: number,
): { key: string; name: string } | null {
  const reference = fontSizePx / brandScale(doc);
  let best: { key: string; name: string; distance: number } | null = null;
  for (const style of tokens.textStyles) {
    const distance = Math.abs(Math.log(style.fontSize / reference));
    if (!best || distance < best.distance) best = { key: style.key, name: style.name, distance };
  }
  return best ? { key: best.key, name: best.name } : null;
}

const format = (value: number) => value.toFixed(1).replace(".", ",");

// ---------------------------------------------------------------------------
// The check
// ---------------------------------------------------------------------------

export function checkDesign(doc: SceneDocument, options: DesignCheckOptions = {}): DesignFinding[] {
  const tokens = options.tokens ?? null;
  const findings: DesignFinding[] = [];
  const seen = new Set<string>();
  const add = (finding: DesignFinding) => {
    if (seen.has(finding.id)) return;
    seen.add(finding.id);
    findings.push(finding);
  };
  const multiPage = doc.pages.length > 1;

  /** Tables and charts: font of the kit, contrast of their texts to what lies below. */
  const checkLabelled = (
    element: Extract<SceneElement, { type: "table" | "chart" }>,
    item: FlatElement,
    below: FlatElement[],
    label: string,
    pageIndex: number,
  ) => {
    const at = { pageIndex, elementId: element.id };
    if (tokens && !tokens.textStyles.some((style) => style.fontFamily === element.fontFamily)) {
      const style = tokens.textStyles.find((entry) => entry.key === "body") ?? tokens.textStyles[0];
      add({
        id: `font:${element.id}`,
        category: "font",
        severity: "warning",
        code: "font-off-brand",
        message: `${label}: Die Schrift „${element.fontFamily}“ gehört nicht zur Marke.`,
        ...at,
        ...(style
          ? {
              fix: {
                kind: "font-family" as const,
                elementId: element.id,
                family: style.fontFamily,
                label: `„${style.fontFamily}“ verwenden`,
              },
            }
          : {}),
      });
    }
    const backdrop = backdropAt(
      doc,
      pageIndex,
      below,
      center(boundingRect(item.box)),
      options.imageColors,
    );
    if (backdrop.kind === "unknown") return;
    const base = backdrop.colors;
    const pairs: { what: string; text: string; fills: string[]; bold: boolean; size: number }[] =
      [];
    if (element.type === "table") {
      if (element.header) {
        pairs.push({
          what: "Kopfzeile",
          text: element.headerColor,
          fills: base.map((bg) => blend(element.headerFill, bg)),
          bold: true,
          size: element.fontSize,
        });
      }
      const body = base.map((bg) => blend(element.bodyFill, bg));
      const fills = element.zebra
        ? [...body, ...base.map((bg) => blend(element.stripeFill, bg))]
        : body;
      if (!element.header || element.cells.length > 1) {
        pairs.push({
          what: "Zellen",
          text: element.color,
          fills,
          bold: element.fontWeight >= 700,
          size: element.fontSize,
        });
      }
    } else {
      pairs.push({
        what: "Beschriftung",
        text: element.color,
        fills: base,
        bold: false,
        size: element.fontSize * 0.85,
      });
    }
    for (const pair of pairs) {
      const large = isLargeText(doc, pair.size, pair.bold ? 700 : 400);
      const required = large ? 3 : 4.5;
      const ratio = Math.min(
        ...pair.fills.map((bg) => contrastRatio(blend(pair.text, bg, item.opacity), bg)),
      );
      if (ratio >= required) continue;
      add({
        id: `contrast:${element.id}:${pair.what}`,
        category: "contrast",
        severity: ratio < 3 ? "error" : "warning",
        code: "contrast-low",
        message: `${label}: ${pair.what} mit Kontrast ${format(ratio)} : 1 – nötig sind ${format(required)} : 1 (WCAG AA${large ? ", große Schrift" : ""}).`,
        ...at,
      });
    }
  };

  doc.pages.forEach((page, pageIndex) => {
    const pageLabel = multiPage ? `Seite ${pageIndex + 1}: ` : "";

    // Page background
    if (tokens && typeof page.background === "string" && parseHex(page.background).a > 0) {
      const linked = page.brand?.background && brandColor(tokens, page.brand.background);
      const value = opaqueHex(page.background);
      if (!linked && !NEUTRALS.has(value)) {
        const exact = kitColorByValue(tokens, value);
        const nearest = exact ?? nearestBrandColor(tokens, value);
        if (exact) {
          add({
            id: `link:page:${page.id}`,
            category: "link",
            severity: "info",
            code: "brand-unlinked",
            message: `${pageLabel}Der Seitenhintergrund entspricht „${exact.name}“, ist aber nicht mit der Marke verknüpft.`,
            pageIndex,
            fix: { kind: "page-background", pageIndex, key: exact.key, label: "Verknüpfen" },
          });
        } else if (nearest) {
          add({
            id: `color:page:${page.id}`,
            category: "color",
            severity: "warning",
            code: "color-off-brand",
            message: `${pageLabel}Der Seitenhintergrund ${value} ist keine Markenfarbe.`,
            pageIndex,
            fix: {
              kind: "page-background",
              pageIndex,
              key: nearest.key,
              label: `„${nearest.name}“ verwenden`,
            },
          });
        }
      }
    }

    const flat = flattenPage(doc, pageIndex);
    flat.forEach((item, index) => {
      if (item.hidden) return;
      const { element, box } = item;
      const label = `${pageLabel}${elementLabel(element)}`;
      const at = { pageIndex, elementId: element.id };

      // Colours
      if (tokens) {
        for (const use of colorUses(element)) {
          if (parseHex(use.value).a < 0.02) continue;
          const key = element.brand?.[use.slot];
          if (key && brandColor(tokens, key)) continue;
          const value = opaqueHex(use.value);
          if (NEUTRALS.has(value)) continue;
          const exact = kitColorByValue(tokens, value);
          if (exact) {
            add({
              id: `link:${element.id}:${use.slot}`,
              category: "link",
              severity: "info",
              code: "brand-unlinked",
              message: `${label}: ${use.what} entspricht „${exact.name}“, ist aber nicht verknüpft.`,
              ...at,
              fix: {
                kind: "bind-color",
                elementId: element.id,
                slot: use.slot,
                key: exact.key,
                label: "Verknüpfen",
              },
            });
            continue;
          }
          const nearest = nearestBrandColor(tokens, value);
          if (!nearest) continue;
          add({
            id: `color:${element.id}:${use.slot}`,
            category: "color",
            severity: "warning",
            code: "color-off-brand",
            message: `${label}: ${use.what} ${value} ist keine Markenfarbe (am nächsten: „${nearest.name}“).`,
            ...at,
            fix: {
              kind: "bind-color",
              elementId: element.id,
              slot: use.slot,
              key: nearest.key,
              label: `„${nearest.name}“ verwenden`,
            },
          });
        }
      }

      if (element.type === "table" || element.type === "chart") {
        checkLabelled(element, item, flat.slice(0, index), label, pageIndex);
        return;
      }
      if (element.type !== "text" || element.text.trim() === "") return;
      const size = smallestFontSize(element);

      // Fonts
      if (tokens) {
        const linked = element.brand?.text && brandTextStyle(tokens, element.brand.text);
        const allowed = new Set(tokens.textStyles.map((style) => style.fontFamily));
        const families = textFamilies(element).filter(
          (family) => !allowed.has(family) && !(linked && family === element.style.fontFamily),
        );
        if (families.length > 0) {
          const style = closestTextStyle(tokens, doc, element.style.fontSize);
          add({
            id: `font:${element.id}`,
            category: "font",
            severity: "warning",
            code: "font-off-brand",
            message: `${label}: Die Schrift ${families.map((family) => `„${family}“`).join(", ")} gehört nicht zur Marke.`,
            ...at,
            ...(style
              ? {
                  fix: {
                    kind: "text-style" as const,
                    elementId: element.id,
                    key: style.key,
                    label: `Stil „${style.name}“`,
                  },
                }
              : {}),
          });
        }
      }

      // Contrast
      const bounds = boundingRect(box);
      const backdrop = backdropAt(
        doc,
        pageIndex,
        flat.slice(0, index),
        center(bounds),
        options.imageColors,
      );
      if (backdrop.kind === "unknown") {
        add({
          id: `contrast-unknown:${element.id}`,
          category: "contrast",
          severity: "info",
          code: "contrast-unknown",
          message: `${label}: Kontrast nicht prüfbar (liegt auf ${backdrop.source}). Bitte selbst prüfen.`,
          ...at,
        });
      } else {
        const large = isLargeText(doc, size, element.style.fontWeight);
        const required = large ? 3 : 4.5;
        const ratioOf = (color: string) =>
          Math.min(
            ...backdrop.colors.map((bg) => contrastRatio(blend(color, bg, item.opacity), bg)),
          );
        const ratio = ratioOf(element.style.color);
        if (ratio < required) {
          let fix: DesignFinding["fix"];
          const candidates = (tokens?.colors ?? [])
            .map((entry) => ({ entry, ratio: ratioOf(entry.color) }))
            .sort((a, b) => b.ratio - a.ratio);
          const best = candidates[0];
          if (best && best.ratio >= required) {
            fix = {
              kind: "bind-color",
              elementId: element.id,
              slot: "color",
              key: best.entry.key,
              label: `„${best.entry.name}“ (${format(best.ratio)} : 1)`,
            };
          } else {
            const black = ratioOf("#000000");
            const white = ratioOf("#ffffff");
            const color = black >= white ? "#000000" : "#ffffff";
            fix = {
              kind: "text-color",
              elementId: element.id,
              color,
              label: `${color === "#000000" ? "Schwarz" : "Weiß"} (${format(Math.max(black, white))} : 1)`,
            };
          }
          add({
            id: `contrast:${element.id}`,
            category: "contrast",
            severity: ratio < 3 ? "error" : "warning",
            code: "contrast-low",
            message: `${label}: Kontrast ${format(ratio)} : 1 ${backdrop.source} – nötig sind ${format(required)} : 1 (WCAG AA${large ? ", große Schrift" : ""}).`,
            ...at,
            fix,
          });
        }
      }

      // Size and edges (print: via the print preflight below)
      if (!doc.format.print) {
        const { value } = textMeasure(doc, size);
        if (value < MIN_SCREEN_TEXT_PX) {
          add({
            id: `size:${element.id}`,
            category: "size",
            severity: "warning",
            code: "text-small",
            message: `${label} ist sehr klein (${format(value)} px bei 1080 px Breite, empfohlen mindestens ${MIN_SCREEN_TEXT_PX} px). Auf dem Handy ist das kaum lesbar.`,
            ...at,
          });
        }
        const outside =
          bounds.x < -0.5 ||
          bounds.y < -0.5 ||
          bounds.x + bounds.width > pageSize(doc, page).width + 0.5 ||
          bounds.y + bounds.height > pageSize(doc, page).height + 0.5;
        if (outside) {
          add({
            id: `edge:${element.id}`,
            category: "size",
            severity: "warning",
            code: "text-outside",
            message: `${label} ragt über den Rand und wird abgeschnitten.`,
            ...at,
          });
        }
      }
    });
  });

  // Print checks: reuse the print preflight.
  if (doc.format.print) {
    for (const issue of preflightDocument(doc, options.assets ?? {}, options.print ?? {})) {
      const category: DesignCheckCategory =
        issue.code === "text-small" || issue.code === "safe-zone" ? "size" : "print";
      add({
        id: `print:${issue.code}:${issue.elementId ?? issue.pageIndex ?? "doc"}`,
        category,
        severity: issue.severity,
        code: issue.code,
        message: issue.message,
        ...(issue.pageIndex !== undefined ? { pageIndex: issue.pageIndex } : {}),
        ...(issue.elementId !== undefined ? { elementId: issue.elementId } : {}),
      });
    }
  }

  const order = { error: 0, warning: 1, info: 2 } as const;
  return findings.sort((a, b) => order[a.severity] - order[b.severity]);
}

/** Findings that count for the badge (errors and warnings). */
export function countDesignProblems(findings: readonly DesignFinding[]): number {
  return findings.filter((finding) => finding.severity !== "info").length;
}

// ---------------------------------------------------------------------------
// Fixes
// ---------------------------------------------------------------------------

/** What a fix changes; two fixes with the same target must not both run. */
export function designFixTarget(fix: DesignFix): string {
  switch (fix.kind) {
    case "bind-color":
      return `${fix.elementId}:${fix.slot}`;
    case "text-color":
      return `${fix.elementId}:color`;
    case "text-style":
    case "font-family":
      return `${fix.elementId}:text`;
    case "page-background":
      return `page:${fix.pageIndex}`;
  }
}

/**
 * Applies a fix to a document (mutating; for Immer drafts). Returns false if
 * it no longer applies.
 */
export function applyDesignFix(
  doc: SceneDocument,
  fix: DesignFix,
  tokens: BrandTokens | null,
): boolean {
  switch (fix.kind) {
    case "bind-color": {
      const element = doc.elements[fix.elementId];
      return element && tokens ? bindColor(element, fix.slot, fix.key, tokens) : false;
    }
    case "text-style": {
      const element = doc.elements[fix.elementId];
      if (!element || !tokens) return false;
      return bindTextStyle(element, fix.key, tokens, brandScale(doc), { withColor: false });
    }
    case "text-color": {
      const element = doc.elements[fix.elementId];
      if (element?.type !== "text") return false;
      element.style.color = fix.color;
      if (element.brand?.color) {
        delete element.brand.color;
        if (Object.keys(element.brand).length === 0) delete element.brand;
      }
      return true;
    }
    case "font-family": {
      const element = doc.elements[fix.elementId];
      if (element?.type !== "table" && element?.type !== "chart") return false;
      element.fontFamily = fix.family;
      return true;
    }
    case "page-background": {
      const page = doc.pages[fix.pageIndex];
      const color = tokens && brandColor(tokens, fix.key)?.color;
      if (!page || !color) return false;
      page.background = color;
      page.brand = { ...page.brand, background: fix.key };
      return true;
    }
  }
}

const FIX_PRIORITY: Record<DesignCheckCategory, number> = {
  contrast: 0,
  link: 1,
  color: 2,
  font: 3,
  size: 4,
  print: 5,
};

/**
 * "Alle beheben": applies every fix, contrast fixes first (they also pick a
 * brand colour), each target once, and re-checks up to three times so fixes
 * that create new findings (a brand colour with too little contrast) settle.
 * Mutating; returns the number of applied fixes.
 */
export function applyAllDesignFixes(doc: SceneDocument, options: DesignCheckOptions = {}): number {
  let applied = 0;
  const done = new Set<string>();
  for (let pass = 0; pass < 3; pass++) {
    const fixes = checkDesign(doc, options)
      .filter((finding) => finding.fix)
      .sort((a, b) => FIX_PRIORITY[a.category] - FIX_PRIORITY[b.category]);
    const targets = new Set<string>();
    let changed = 0;
    for (const finding of fixes) {
      const fix = finding.fix;
      if (!fix) continue;
      const target = designFixTarget(fix);
      // A target fixed for contrast is not changed back to the nearest colour.
      if (targets.has(target) || (done.has(target) && finding.category !== "contrast")) continue;
      targets.add(target);
      if (applyDesignFix(doc, fix, options.tokens ?? null)) changed++;
    }
    for (const target of targets) done.add(target);
    applied += changed;
    if (changed === 0) break;
  }
  return applied;
}
